import { Icon, Spinner } from "@blueprintjs/core";
import * as React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { DrupalTitleRequestDto, EPlatform, ERequestState, RequestDto, TitlesRequestApi } from "../../../../api";
import { PageContainer } from "../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalRequestResume, DrupalStepper } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { DrupalTitleRequestForm } from "./DrupalTitleRequestForm";

export interface IDrupalTitleRequestPageProps {}

const StyledInfoContainer = styled.div`
  border: 2px solid #3f76f9;
  background-color: #c0e4fc;
  margin: 0.5em 5em;
  padding: 0.5rem;
  text-align: center;
  border-radius: 10px;
`;

const StyledImage = styled(Icon)`
  color: #1e90ff;
  size: 9px;
`;

export const DrupalTitleRequestPage: React.FunctionComponent<IDrupalTitleRequestPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const titleRequestId = React.useMemo(() => +id, [id]);

  const { t } = useTl();
  const api = useApiService(TitlesRequestApi);

  const [currentStep, setCurrentStep] = React.useState(0);
  const [files, setFiles] = React.useState<any[]>([]);

  const saveCallback = React.useCallback(
    async (d: DrupalTitleRequestDto) => {
      try {
        const res = await api.saveDrupalTitleRequest(d);
        await api.importFile(
          res?.titleRequestId,
          files?.map((f) => ({ data: f.file, fileName: f.filename }))
        );
        return res;
      } catch {
        const previous = currentStep - 1;
        setCurrentStep(previous);
      }
    },
    [api, currentStep, files]
  );

  const { data, loading, saveItem, saving } = useCrudApi<DrupalTitleRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (currentId = titleRequestId) =>
          currentId > 0
            ? api.getDrupalTitleRequest(currentId)
            : new DrupalTitleRequestDto({
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                  platform: EPlatform.PORTAL,
                }),
              }).toJSON(),
        saveApiFn: (d: DrupalTitleRequestDto) => saveCallback(d),
        onSavedRoute: (d) =>
          d.request.status === ERequestState.BILLED
            ? `${EDrupalRoutes.titleRequest}/${d.titleRequestId}`
            : EDrupalRoutes.requestList,
        deleteApiFn: () => {},
      }),
      [api, saveCallback, titleRequestId]
    ),
    [titleRequestId]
  );

  return loading ? (
    <Spinner />
  ) : (
    <PageContainer>
      <DrupalFieldSet
        title={
          currentStep === 2
            ? `${t(ETLCodes.Payment)}: ${t(ETLCodes.TitleRequest)}`
            : `${t(ETLCodes.NewRequest)}: ${t(ETLCodes.TitleRequest)}`
        }
      >
        {+id <= 0 ? <DrupalStepper stepNumber={currentStep} /> : null}
        <div>
          {+id <= 0 && data?.request?.status === ERequestState.REQUESTED ? (
            <>
              <StyledInfoContainer>
                <StyledImage icon="info-sign" />
                {t(ETLCodes.DrupalTitleRequestInfo)}
              </StyledInfoContainer>
              <DrupalTitleRequestForm
                stepNumber={currentStep}
                initialdata={data}
                loading={saving}
                onStepChange={(newStep) => setCurrentStep(newStep)}
                save={(formikdata) => saveItem(formikdata)}
                files={files}
                setFiles={setFiles}
              />
            </>
          ) : saving ? (
            <Spinner />
          ) : (
            <DrupalRequestResume
              requestForm={
                <DrupalTitleRequestForm
                  showStepper={data?.request?.status !== ERequestState.BILLED}
                  stepNumber={currentStep}
                  initialdata={data}
                  loading={saving}
                  onStepChange={(newStep) => setCurrentStep(newStep)}
                  save={(formikdata) => saveItem(formikdata)}
                  files={files}
                  setFiles={setFiles}
                />
              }
              requestDto={data?.request}
              bill={null}
            />
          )}
        </div>
      </DrupalFieldSet>
    </PageContainer>
  );
};
